/**
 * DEVELOPMENT-ONLY booking simulator.
 *
 * Lets the checkout UI be exercised on the local dev server before Supabase
 * and SumUp exist. It mirrors the server rules in
 * supabase/migrations/20261014000400_bookings.sql (people-based capacity,
 * expiring holds, idempotent retries, verified-payment-only confirmation),
 * but it is NOT the source of truth and never runs in production builds.
 * All numbers it produces (including capacity) are simulated test values.
 */
export function createBookingSimulator({ capacity, priceCents, currency = "EUR", maxGuestsPerBooking = 10, holdMinutes = 15, now = () => Date.now() }) {
  const bookings = new Map(); // reference -> booking
  const byKey = new Map(); // idempotencyKey -> reference

  const holdLive = (b) => b.status === "pending_payment" && b.holdExpiresAt > now();
  const taken = (excludeRef) =>
    [...bookings.values()]
      .filter((b) => b.reference !== excludeRef && (b.status === "confirmed" || holdLive(b)))
      .reduce((sum, b) => sum + b.guests, 0);

  const expireHolds = () => {
    for (const b of bookings.values()) {
      if (b.status === "pending_payment" && b.holdExpiresAt <= now()) {
        b.status = "expired";
        b.holdExpiresAt = null;
      }
    }
  };

  const view = (b) => ({
    reference: b.reference,
    status: b.status,
    guests: b.guests,
    unit_price_cents: b.unitPriceCents,
    total_cents: b.totalCents,
    currency,
    hold_expires_at: b.holdExpiresAt ? new Date(b.holdExpiresAt).toISOString() : null,
    payment_mode: "test",
    payment: { provider: "simulation", status: b.paymentStatus, checkout_id: `sim_${b.reference}` },
  });

  const fail = (code, detail) => {
    const err = new Error(code);
    err.code = code;
    err.detail = detail;
    throw err;
  };

  return {
    availability() {
      const remaining = Math.max(capacity - taken(), 0);
      const state = remaining === 0 ? "sold_out" : "on_sale";
      return {
        state,
        on_sale: state === "on_sale",
        remaining: state === "on_sale" ? remaining : null,
        price_cents: priceCents,
        currency,
        max_guests_per_booking: maxGuestsPerBooking,
        hold_minutes: holdMinutes,
        simulated: true,
      };
    },

    reserve({ guests, name, email, idempotencyKey, accessToken }) {
      const existingRef = byKey.get(idempotencyKey);
      if (existingRef) {
        const existing = bookings.get(existingRef);
        if (existing.accessToken !== accessToken) fail("idempotency_conflict");
        return view(existing);
      }
      if (!Number.isInteger(guests) || guests < 1 || guests > maxGuestsPerBooking) fail("invalid_guest_count");
      if (!name || !email) fail("invalid_input");
      const remaining = capacity - taken();
      if (guests > remaining) fail("insufficient_capacity", Math.max(remaining, 0));

      let reference;
      do {
        reference = `GW-${Math.random().toString(16).slice(2, 10).toUpperCase().padEnd(8, "0")}`;
      } while (bookings.has(reference));

      const b = {
        reference,
        guests,
        unitPriceCents: priceCents,
        totalCents: priceCents * guests,
        status: "pending_payment",
        paymentStatus: "pending",
        holdExpiresAt: now() + holdMinutes * 60_000,
        accessToken,
      };
      bookings.set(reference, b);
      byKey.set(idempotencyKey, reference);
      return view(b);
    },

    get(reference, accessToken) {
      expireHolds();
      const b = bookings.get(reference);
      if (!b || b.accessToken !== accessToken) fail("booking_not_found");
      return view(b);
    },

    /** Simulated provider outcome, then the same rules as record_payment_result. */
    pay(reference, accessToken, outcome) {
      const b = bookings.get(reference);
      if (!b || b.accessToken !== accessToken) fail("booking_not_found");
      if (b.paymentStatus === "paid") return view(b); // idempotent
      if (outcome === "paid") {
        b.paymentStatus = "paid";
        if (holdLive(b)) {
          b.status = "confirmed";
        } else if (b.status === "pending_payment" || b.status === "expired") {
          b.status = capacity - taken(b.reference) >= b.guests ? "confirmed" : "cancelled";
          b.refundRequired = b.status === "cancelled";
        }
        b.holdExpiresAt = null;
      } else if (outcome === "failed" || outcome === "cancelled") {
        b.paymentStatus = outcome;
        if (b.status === "pending_payment") {
          b.status = "cancelled";
          b.holdExpiresAt = null;
        }
      }
      return view(b);
    },
  };
}
